import { useEffect, useRef } from 'react';
import { PALETTES, sampleGradient, type Palette } from '@wg/shared';
import { useViewStore } from '@/stores/viewStore.js';
import { applyPalette } from './applyPalette.js';
import clsx from 'clsx';

export function PalettePicker() {
  const { paletteId, setPalette } = useViewStore();
  const palettes = Object.values(PALETTES);
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => { applyPalette(paletteId); }, [paletteId]);

  const focusAt = (idx: number) => {
    const n = palettes.length;
    const wrapped = ((idx % n) + n) % n;
    const target = palettes[wrapped];
    if (!target) return;
    setPalette(target.id);
    buttonsRef.current[wrapped]?.focus();
  };

  const onKeyDown = (currentIdx: number) => (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusAt(currentIdx + 1);
        return;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusAt(currentIdx - 1);
        return;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        return;
      case 'End':
        e.preventDefault();
        focusAt(palettes.length - 1);
        return;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label="Palette"
      className="flex gap-2 overflow-x-auto p-3 border-t border-neutral-800 bg-neutral-900/50"
    >
      {palettes.map((p, idx) => {
        const checked = paletteId === p.id;
        // Axe's static analyser can't evaluate conditional ARIA values, so
        // render two branches with a LITERAL aria-checked on each. Shared
        // props are hoisted into a const to avoid duplicating the JSX.
        const refCallback = (el: HTMLButtonElement | null) => {
          buttonsRef.current[idx] = el;
          // Set the live gradient via the ref instead of style={{…}}; keeps
          // webhint's no-inline-styles rule happy. The gradient is runtime-
          // computed per palette, so it can't live in a stylesheet.
          if (el) el.style.background = gradientCss(p);

        };
        const className = clsx(
          'group relative h-14 min-w-[128px] rounded-md px-3 text-sm font-medium transition',
          'border',
          checked ? 'border-white' : 'border-neutral-700 hover:border-neutral-400',
        );
        const handleKeyDown = onKeyDown(idx);
        const selectThis = () => setPalette(p.id);
        const label = (
          <span className="relative mix-blend-difference text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.6)]">
            {p.name}
          </span>
        );
        return checked ? (
          <button
            key={p.id}
            type="button"
            ref={refCallback}
            role="radio"
            aria-checked="true"
            tabIndex={0}
            onClick={selectThis}
            onKeyDown={handleKeyDown}
            className={className}
          >
            {label}
          </button>
        ) : (
          <button
            key={p.id}
            type="button"
            ref={refCallback}
            role="radio"
            aria-checked="false"
            tabIndex={-1}
            onClick={selectThis}
            onKeyDown={handleKeyDown}
            className={className}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// sampleGradient caches interpolators by palette.id; pass the real palette so the
// cache keys each preview correctly (the plan's `{ id: 'preview', ... }` shim caused
// every tile after the first to render the first palette's gradient).
function gradientCss(p: Palette): string {
  const sample = (t: number) => {
    const [r, g, b] = sampleGradient(p, t);
    return `rgb(${r},${g},${b}) ${Math.round(t * 100)}%`;
  };
  return `linear-gradient(to right, ${[0, 0.25, 0.5, 0.75, 1].map(sample).join(', ')})`;
}
